import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, CornerDownLeft } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { POPULAR_SEARCHES } from "@/lib/searchSynonyms";
import {
  searchSite,
  groupResults,
  highlightParts,
  TYPE_LABELS,
  type SiteSearchResult,
} from "@/lib/siteSearch";

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const RECENT_KEY = "ocig-recent-searches";

function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? (JSON.parse(raw) as string[]).slice(0, 5) : [];
  } catch {
    return [];
  }
}

function saveRecent(term: string) {
  try {
    const next = [term, ...readRecent().filter((t) => t.toLowerCase() !== term.toLowerCase())].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* storage disabled — recents are a nicety, not a requirement */
  }
}

const Highlighted = ({ text, query }: { text: string; query: string }) => (
  <>
    {highlightParts(text, query).map((part, i) =>
      part.match ? (
        <mark key={i} className="bg-primary/20 text-inherit rounded-sm px-0.5">{part.text}</mark>
      ) : (
        <span key={i}>{part.text}</span>
      )
    )}
  </>
);

const SearchDialog = ({ open, onOpenChange }: SearchDialogProps) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SiteSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [recent, setRecent] = useState<string[]>([]);
  const requestId = useRef(0);
  const navigate = useNavigate();

  const search = useCallback(async (q: string) => {
    const myRequest = ++requestId.current;
    if (q.trim().length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const rows = await searchSite(q, 24);
    // A slower earlier keystroke must never overwrite fresher results.
    if (myRequest !== requestId.current) return;
    setResults(rows);
    setActiveIndex(0);
    setSearching(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => search(query), 220);
    return () => clearTimeout(timer);
  }, [query, search]);

  useEffect(() => {
    if (open) {
      setRecent(readRecent());
    } else {
      setQuery("");
      setResults([]);
      setActiveIndex(0);
    }
  }, [open]);

  const groups = useMemo(() => groupResults(results), [results]);
  // Flat order mirrors what's rendered, so arrow keys walk the visible list.
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const go = (result: SiteSearchResult) => {
    if (query.trim()) saveRecent(query.trim());
    onOpenChange(false);
    navigate(result.slug);
  };

  const seeAll = () => {
    const q = query.trim();
    if (!q) return;
    saveRecent(q);
    onOpenChange(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = flat[activeIndex];
      if (target) go(target);
      else seeAll();
    }
  };

  const showEmptyState = query.trim().length < 2;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl p-0 gap-0 overflow-hidden [&>button:last-child]:hidden">
        <DialogTitle className="sr-only">Search the site</DialogTitle>
        <div className="flex items-center border-b border-border px-4">
          <Search className="h-4 w-4 text-muted-foreground mr-2 flex-shrink-0" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search recipes, ingredients, posts, shop…"
            className="border-0 focus-visible:ring-0 h-12 text-sm font-body"
            autoFocus
          />
          {query && (
            <button type="button" aria-label="Clear search query" onClick={() => setQuery("")} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="max-h-[26rem] overflow-y-auto p-2">
          {showEmptyState && (
            <div className="px-2 py-3 space-y-4">
              {recent.length > 0 && (
                <div>
                  <p className="text-[10px] font-heading uppercase tracking-wider text-muted-foreground mb-2">Recent searches</p>
                  <div className="flex flex-wrap gap-2">
                    {recent.map((term) => (
                      <button
                        key={term}
                        type="button"
                        onClick={() => setQuery(term)}
                        className="text-xs font-body rounded-full border border-border px-3 py-1 hover:bg-accent transition-colors"
                      >
                        {term}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <p className="text-[10px] font-heading uppercase tracking-wider text-muted-foreground mb-2">Popular searches</p>
                <div className="flex flex-wrap gap-2">
                  {POPULAR_SEARCHES.map((term) => (
                    <button
                      key={term}
                      type="button"
                      onClick={() => setQuery(term)}
                      className="text-xs font-body rounded-full border border-border px-3 py-1 hover:bg-accent transition-colors"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!showEmptyState && searching && (
            <p className="text-sm text-muted-foreground text-center py-6 font-body">Searching…</p>
          )}

          {!showEmptyState && !searching && results.length === 0 && (
            <div className="text-center py-6 px-3">
              <p className="text-sm text-muted-foreground font-body mb-3">
                Nothing matched “{query.trim()}”. Try one of these:
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {POPULAR_SEARCHES.slice(0, 6).map((term) => (
                  <button
                    key={term}
                    type="button"
                    onClick={() => setQuery(term)}
                    className="text-xs font-body rounded-full border border-border px-3 py-1 hover:bg-accent transition-colors"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!searching && groups.map((group) => (
            <div key={group.type} className="mb-2">
              <p className="text-[10px] font-heading uppercase tracking-wider text-muted-foreground px-3 py-1">
                {TYPE_LABELS[group.type]}
              </p>
              {group.items.map((r) => {
                const index = flat.indexOf(r);
                return (
                  <Link
                    key={r.slug}
                    to={r.slug}
                    onClick={(e) => { e.preventDefault(); go(r); }}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${index === activeIndex ? "bg-accent" : "hover:bg-accent"}`}
                  >
                    {r.image ? (
                      <img src={r.image} alt="" loading="lazy" decoding="async" className="h-9 w-9 rounded-md object-cover flex-shrink-0" />
                    ) : (
                      <span className="h-9 w-9 rounded-md bg-muted flex-shrink-0" />
                    )}
                    <span className="min-w-0">
                      <span className="block text-sm font-heading font-bold truncate">
                        <Highlighted text={r.title} query={query} />
                      </span>
                      {r.excerpt && (
                        <span className="block text-xs text-muted-foreground font-body line-clamp-1">
                          <Highlighted text={r.excerpt} query={query} />
                        </span>
                      )}
                    </span>
                  </Link>
                );
              })}
            </div>
          ))}

          {!showEmptyState && !searching && results.length > 0 && (
            <button
              type="button"
              onClick={seeAll}
              className="w-full flex items-center justify-center gap-2 text-xs font-heading uppercase tracking-wide py-2.5 rounded-lg border border-border hover:bg-accent transition-colors"
            >
              See all results for “{query.trim()}” <CornerDownLeft className="h-3 w-3" />
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
